import {
  Collapse,
  Grid,
  IconButton,
  MenuItem,
  Switch,
  Typography,
} from '@material-ui/core';
import { ArrowDropDown, ArrowDropUp, Info } from '@material-ui/icons';
import {
  FormikCheckboxField,
  FormikContextTypeEnhanced,
  FormikCurrencyField,
  FormikNumberField,
  FormikTextField,
  useFormikEnhanced,
} from '@superdispatch/forms';
import { useDeepEqualMemo, useValueObserver } from '@superdispatch/hooks';
import { VEHICLE_TYPES, formatVehicleType } from '@superdispatch/sdk';
import {
  Column,
  Columns,
  Inline,
  Stack,
  useSnackbarStack,
} from '@superdispatch/ui';
import { Box, Button } from '@superdispatch/ui-lab';
import { toFinite, toString } from 'es-toolkit/compat';
import { FormikProvider, useFormikContext } from 'formik';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { isWebView } from 'shared/constants/AppTypeConstants';
import { MobileAppBridge } from 'shared/data/MobileAppBridge';
import { FormikMultiSelectField } from 'shared/form/FormikMultiSelectField';
import { ScrollToError } from 'shared/form/ScrollToError';
import { logInfo } from 'shared/helpers/Logger';
import { SuperPayIcon } from 'shared/icons/SuperPayLabel';
import { SwapIcon } from 'shared/icons/SwapIcon';
import {
  convertPricePerKmToPricePerMile,
  convertPricePerMileToPricePerKm,
} from 'shared/modules/loadboard/LoadboardUtils';
import {
  canHaveRadius,
  composeSavedSearchTitle,
  formatPaymentTermsLabel,
  formatPaymentTermsValue,
  isEmptySearchCriteria,
  isValidLATRSearchCriteria,
  paymentGroups,
} from 'shared/modules/loadboard/PostingSearchCriteriaDTO';
import { bypassNavigationPrompt } from 'shared/routing/NavigationBlock';
import styled from 'styled-components';
import {
  editSavedSearch,
  useCreateSavedSearch,
} from '../../data/LoadboardSavedSearchesAPI';
import {
  PostingSavedSearchDTO,
  PostingSavedSearchPayloadDTO,
  alertDurationOptions,
  formatAlertCreateMessage,
  isSavedSearchMuted,
  postingSavedSearchPayloadSchema,
  postingSavedSearchSchema,
} from '../../data/dto/PostingSavedSearchDTO';
import { trackLoadboardEvent } from '../LoadboardAnalytics';
import { LATRCheckboxField } from '../postings/LATRCheckboxField';
import { LATROffRouteDistanceField } from '../postings/latr/LATROffRouteDistanceField';
import { LATRWaypointsField } from '../postings/latr/LATRWaypointsField';
import { AdvancedSearchDateField } from './AdvancedSearchDateField';
import { AdvancedSearchRadiusField } from './AdvancedSearchRadiusField';
import { FormPostingSearchCriteriaVenueField } from './FormPostingSearchCriteriaVenueField';
import { ShippersFilter } from './ShippersFilter';

const vehicleTypes = VEHICLE_TYPES.slice();

function enumToBoolean<T extends string>(
  value: T | undefined,
  trueValue: T,
  falseValue: T,
): boolean | undefined {
  switch (value) {
    case trueValue:
      return true;
    case falseValue:
      return false;

    default:
      return undefined;
  }
}

function booleanToEnum<T extends string>(
  value: unknown,
  trueValue: T,
  falseValue: T,
): T | '' {
  switch (value) {
    case true:
      return trueValue;
    case false:
      return falseValue;
    default:
      return '';
  }
}

export type AdvancedSearchFormMode =
  | 'update'
  | 'save-search'
  | 'search'
  | 'create';

interface FormOptions {
  mode: AdvancedSearchFormMode | undefined;
  defaultValues: PostingSavedSearchDTO;
  onSearch: (values: PostingSavedSearchDTO) => void;
  onSubmitSuccess: (values: PostingSavedSearchDTO) => void;
  onValidationError?: () => void;
}

interface FormResult {
  form: FormikContextTypeEnhanced<
    PostingSavedSearchPayloadDTO,
    PostingSavedSearchDTO
  >;

  isFormEmpty: boolean;
  clearValues: () => void;
  canEditPickupRadius: boolean;
  canEditDeliveryRadius: boolean;

  isFieldsExpanded: boolean;
  expandFields: Dispatch<SetStateAction<boolean>>;
}

export function useAdvancedSearchForm({
  mode,
  onSearch,
  onSubmitSuccess,
  onValidationError,
  defaultValues: defaultValuesProp,
}: FormOptions): FormResult {
  const { addSnackbar } = useSnackbarStack();

  const [isFieldsExpanded, expandFields] = useState(
    !MobileAppBridge.isInjected(),
  );

  const { mutateAsync: createSavedSearch } = useCreateSavedSearch();

  const initialValues = useDeepEqualMemo<PostingSavedSearchPayloadDTO>(
    () => ({
      ...defaultValuesProp,
      alert_duration: defaultValuesProp.alert_duration || 'week',
      alert_enabled: !isSavedSearchMuted(defaultValuesProp),
    }),
    [defaultValuesProp],
  );
  const formik = useFormikEnhanced<
    PostingSavedSearchPayloadDTO,
    PostingSavedSearchDTO
  >({
    initialValues,
    enableReinitialize: false,
    validateOnBlur: true,
    onSubmit: (values) => {
      logInfo('Submit advanced search form', { mode, values });
      switch (mode) {
        case 'update':
          return editSavedSearch(defaultValuesProp.guid, values);
        case 'create':
        case 'save-search':
          return createSavedSearch({ ...values, alert_enabled: true });
        default:
          return Promise.resolve(postingSavedSearchSchema.cast(values));
      }
    },
    onSubmitSuccess: (values) => {
      switch (mode) {
        case 'search': {
          bypassNavigationPrompt(() => {
            onSearch(values);
          });
          return;
        }
        case 'update':
          addSnackbar('Load Alert Updated.', { variant: 'success' });
          trackLoadboardEvent({
            name: 'SLB: Updated Load Alert Criteria',
            savedSearch: values,
            prevSavedSearch: defaultValuesProp,
          });
          break;
        case 'create':
        case 'save-search':
          if (values.alert_duration) {
            addSnackbar(formatAlertCreateMessage(values.alert_duration), {
              variant: 'success',
            });
          }
          trackLoadboardEvent({
            name: 'SLB: Created Load Alert',
            savedSearch: values,
          });
          break;
      }

      bypassNavigationPrompt(() => {
        onSubmitSuccess(values);
      });
    },
    onSubmitFailure: (error) => {
      addSnackbar(error.message, { variant: 'error' });
    },
  });
  const { values, setFieldValue, setFieldTouched, errors, touched } = formik;

  const isFormEmpty = useDeepEqualMemo(
    () =>
      mode !== 'save-search' &&
      !values.description &&
      isEmptySearchCriteria(values.search_criteria),
    [mode, values],
  );

  const canEditPickupRadius = values.search_criteria.pickup_venues.some(
    (venue) => canHaveRadius(venue),
  );

  const canEditDeliveryRadius = values.search_criteria.delivery_venues.some(
    (venue) => canHaveRadius(venue),
  );

  const clearValues = useCallback(() => {
    formik.resetForm({ values: postingSavedSearchPayloadSchema.cast({}) });
  }, [formik]);

  const isLATRValid = isValidLATRSearchCriteria(values.search_criteria);
  const { search_along_route: isAlongRoute } = values.search_criteria;

  useValueObserver(initialValues, () => {
    formik.resetForm({ values: initialValues });
  });

  useEffect(() => {
    const { search_criteria } = initialValues;
    if (MobileAppBridge.isInjected()) {
      expandFields((prev) => {
        const hasValue = [
          search_criteria.price,
          search_criteria.price_per_km,
          search_criteria.vehicle_types,
          search_criteria.vehicle_count,
          search_criteria.max_vehicle_count,
          search_criteria.payment.terms,
          search_criteria.shipper.include,
          search_criteria.shipper.exclude,
          search_criteria.transport_type,
          search_criteria.vehicle_is_inoperable,
        ].some((x) => (Array.isArray(x) ? x.length : x != null));

        return !mode ? false : prev || hasValue;
      });
    }
  }, [mode, initialValues]);

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      onValidationError?.();
    }
  }, [errors, onValidationError]);

  useEffect(() => {
    if (
      mode &&
      mode !== 'search' &&
      !touched.description &&
      !initialValues.description
    ) {
      const nextDescription = composeSavedSearchTitle(
        mode === 'update'
          ? initialValues.search_criteria
          : values.search_criteria,
      );

      if (nextDescription) {
        void setFieldValue('description', nextDescription);
      }
    }
  }, [mode, touched, values, setFieldValue, setFieldTouched, initialValues]);

  useEffect(() => {
    if (!canEditPickupRadius && values.search_criteria.pickup_radius) {
      void setFieldValue('pickup_radius', undefined);
    }
  }, [canEditPickupRadius, setFieldValue, values]);

  useEffect(() => {
    if (!canEditDeliveryRadius && values.search_criteria.delivery_radius) {
      void setFieldValue('delivery_radius', undefined);
    }
  }, [canEditDeliveryRadius, setFieldValue, values]);

  useEffect(() => {
    if (!isLATRValid && isAlongRoute) {
      void setFieldValue('search_criteria.search_along_route', undefined);
      void setFieldValue('search_criteria.distance_off_route', undefined);
      void setFieldValue('search_criteria.latr_waypoints', undefined);
    }
  }, [isLATRValid, isAlongRoute, setFieldValue, values]);

  return {
    form: formik,
    clearValues,
    isFormEmpty,
    expandFields,
    isFieldsExpanded,
    canEditPickupRadius,
    canEditDeliveryRadius,
  };
}

interface AdvancedSearchFormProps extends FormResult {
  mode: AdvancedSearchFormMode | undefined;
  onAddToSavedSearch: () => void;
  isPrivateLoadboard?: boolean;
}

const Styled1 = styled(Grid)`
  margin-top: 16px;
`;

interface AlertSettingsSectionProps {
  mode: AdvancedSearchFormMode | undefined;
  isAlongRoute?: boolean;
  onAddToSavedSearch: () => void;
}

function AlertSettingsSection({
  mode,
  isAlongRoute,
  onAddToSavedSearch,
}: AlertSettingsSectionProps) {
  const { values, setFieldTouched } =
    useFormikContext<PostingSavedSearchPayloadDTO>();

  return (
    <Box
      padding="small"
      borderRadius="small"
      borderWidth="small"
      borderColor="Silver400"
    >
      <Grid container={true} spacing={1}>
        {(mode === 'search' || mode === 'save-search') && (
          <Grid item={true} xs={12}>
            <Columns align="center">
              <Column>
                <label htmlFor="create-load-alert-checkbox">
                  <Typography variant="h4">Create Load Alert</Typography>
                </label>
              </Column>

              <Column width="content">
                <Switch
                  id="create-load-alert-checkbox"
                  size="medium"
                  onChange={onAddToSavedSearch}
                  checked={mode === 'save-search'}
                />
              </Column>
            </Columns>
          </Grid>
        )}

        {mode !== 'search' && (
          <Grid item={true} xs={12}>
            <Stack space="small">
              <Stack space="xsmall">
                {mode === 'update' && (
                  <Typography variant="h4">Load Alert</Typography>
                )}

                <Typography color="textSecondary">
                  You will be receiving SMS and Email alerts.
                </Typography>
              </Stack>

              <FormikTextField
                name="description"
                label="Name"
                fullWidth={true}
                onChange={(event) => {
                  void setFieldTouched('description', true);
                  return toString(event.target.value).slice(0, 100);
                }}
              />

              {mode === 'update' && (
                <FormikCheckboxField
                  name="alert_enabled"
                  label="Mute for Now"
                  format={(_, checked) => !checked}
                  parse={(_, checked) => !checked}
                />
              )}

              <FormikTextField
                name="alert_duration"
                label="Load Alert Duration"
                select={true}
                disabled={mode === 'update' && !values.alert_enabled}
                fullWidth={true}
              >
                {Object.entries(alertDurationOptions).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </FormikTextField>

              {isAlongRoute && (
                <Inline verticalAlign="center" space="xsmall">
                  <Info fontSize="small" color="action" />
                  <Typography color="textSecondary">
                    Will not include Search Along Route loads.
                  </Typography>
                </Inline>
              )}
            </Stack>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

const RotatingIconButton = styled(IconButton)<{ $isRotated: boolean }>`
  transform: rotate(${(props) => (props.$isRotated ? 180 : 0)}deg);
  transition: transform 0.3s ease-in-out;
`;

function SwapButton({ onClick }: { onClick: () => void }) {
  const [isRotated, setIsRotated] = useState(false);

  const handleClick = () => {
    if (isWebView) {
      setIsRotated((prev) => !prev);
    }
    onClick();
  };

  return (
    <RotatingIconButton
      size="small"
      onClick={handleClick}
      $isRotated={isRotated}
      aria-label="Swap pickup and delivery locations"
    >
      <SwapIcon color="action" fontSize={isWebView ? 'small' : 'medium'} />
    </RotatingIconButton>
  );
}

export function AdvancedSearchForm({
  mode,
  form,
  expandFields,
  isFieldsExpanded,
  form: { values, isSubmitting, setFieldValue },
  canEditPickupRadius,
  canEditDeliveryRadius,
  onAddToSavedSearch,
  isPrivateLoadboard,
}: AdvancedSearchFormProps) {
  const { search_along_route: isAlongRoute } = values.search_criteria;

  const validateVenue = useCallback(() => {
    const { delivery_venues, pickup_venues } = values.search_criteria;

    if (mode === 'search') {
      return undefined;
    }

    if (delivery_venues.length || pickup_venues.length) {
      return undefined;
    }

    return 'Pickup or Delivery required';
  }, [mode, values]);

  const swapLocations = () => {
    const { pickup_radius, delivery_radius, pickup_venues, delivery_venues } =
      values.search_criteria;

    void setFieldValue('search_criteria', {
      ...values.search_criteria,
      pickup_radius: delivery_radius,
      delivery_radius: pickup_radius,
      pickup_venues: delivery_venues,
      delivery_venues: pickup_venues,
    });

    trackLoadboardEvent({
      name: 'SLB: UI Interaction',
      type: 'Filter: Clicked Swap Locations',
    });
  };

  return (
    <FormikProvider value={form}>
      <ScrollToError />

      <Grid container={true} spacing={4}>
        <Grid item={true} xs={12}>
          <Grid container={true} spacing={2}>
            <Grid item={true} xs={12}>
              <Columns align="center" space="xsmall">
                <Column width={isWebView ? 'fluid' : 'content'}>
                  <Typography variant="h4">Origin & Destination</Typography>
                </Column>
                <Column width="content">
                  <SwapButton onClick={swapLocations} />
                </Column>
              </Columns>
            </Grid>

            <Grid item={true} xs={12} sm={canEditPickupRadius ? 8 : 12}>
              <FormPostingSearchCriteriaVenueField
                name="search_criteria.pickup_venues"
                disabled={isSubmitting}
                TextFieldProps={{
                  label: 'Pickup',
                  fullWidth: true,
                  placeholder: 'Pickup Cities, States or ZIP',
                }}
                ListboxProps={{ 'aria-label': 'pickup venue options' }}
                validate={validateVenue}
              />
            </Grid>

            {canEditPickupRadius && (
              <Grid item={true} xs={12} sm={4}>
                <AdvancedSearchRadiusField name="search_criteria.pickup_radius" />
              </Grid>
            )}

            <Grid item={true} xs={true} sm={canEditDeliveryRadius ? 8 : 12}>
              <FormPostingSearchCriteriaVenueField
                name="search_criteria.delivery_venues"
                disabled={isSubmitting}
                TextFieldProps={{
                  label: 'Delivery',
                  fullWidth: true,
                  placeholder: 'Delivery Cities, States or ZIP',
                }}
                validate={validateVenue}
              />
            </Grid>

            {canEditDeliveryRadius && (
              <Grid item={true} xs={12} sm={4}>
                <AdvancedSearchRadiusField name="search_criteria.delivery_radius" />
              </Grid>
            )}

            <Grid item={true} xs={12}>
              <Stack space="small">
                <LATRCheckboxField />

                {isAlongRoute && (
                  <Stack space="medium">
                    <Stack space="xsmall">
                      <Typography variant="h4">Loads Along Route</Typography>

                      <LATRWaypointsField name="search_criteria.latr_waypoints" />
                    </Stack>

                    <LATROffRouteDistanceField />
                  </Stack>
                )}
              </Stack>
            </Grid>

            {MobileAppBridge.isInjected() && !isFieldsExpanded && (
              <Grid item={true} xs={12}>
                <Button
                  variant="neutral"
                  fullWidth={true}
                  startIcon={<ArrowDropDown color="action" />}
                  onClick={() => {
                    expandFields(true);
                  }}
                >
                  Advanced Search
                </Button>
              </Grid>
            )}
          </Grid>
        </Grid>

        <Grid
          item={true}
          xs={12}
          style={{ padding: !isFieldsExpanded ? 0 : undefined }}
        >
          <Grid container={true}>
            <Grid item={true} xs={12}>
              <Collapse in={isFieldsExpanded} timeout={{ appear: 200 }}>
                <Grid container={true} spacing={4}>
                  <Grid item={true} xs={12}>
                    <Grid container={true} spacing={2}>
                      <Grid item={true} xs={12}>
                        <Typography variant="h4">Vehicle</Typography>
                      </Grid>

                      <Grid item={true} xs={12}>
                        <FormikMultiSelectField
                          name="search_criteria.vehicle_types"
                          disabled={isSubmitting}
                          options={vehicleTypes}
                          getOptionLabel={formatVehicleType}
                          TextFieldProps={{
                            label: 'Vehicle Type',
                            placeholder:
                              values.search_criteria.vehicle_types.length < 2
                                ? 'Select Vehicles'
                                : '',
                            fullWidth: true,
                          }}
                        />
                      </Grid>

                      <Grid item={true} xs={12} sm={6}>
                        <FormikTextField
                          name="search_criteria.vehicle_is_inoperable"
                          label="Condition"
                          select={true}
                          disabled={isSubmitting}
                          fullWidth={true}
                          parse={(value) =>
                            enumToBoolean(
                              value.target.value,
                              'inoperable',
                              'operable',
                            )
                          }
                          format={(value) =>
                            booleanToEnum(value, 'inoperable', 'operable')
                          }
                          SelectProps={{ displayEmpty: true }}
                          inputProps={{ 'data-testid': 'vehicle-condition' }}
                        >
                          <MenuItem value="">All</MenuItem>
                          <MenuItem value="operable">Operable</MenuItem>
                          <MenuItem value="inoperable">Inoperable</MenuItem>
                        </FormikTextField>
                      </Grid>

                      <Grid item={true} xs={12} sm={6}>
                        <FormikTextField
                          name="search_criteria.transport_type"
                          label="Transport Type"
                          select={true}
                          disabled={isSubmitting}
                          fullWidth={true}
                          SelectProps={{ displayEmpty: true }}
                          format={(value) => (value ? String(value) : '')}
                          parse={(event) => event.target.value || undefined}
                          inputProps={{ 'data-testid': 'transport-type' }}
                        >
                          <MenuItem value="">All</MenuItem>
                          <MenuItem value="OPEN">Open</MenuItem>
                          <MenuItem value="ENCLOSED">Enclosed</MenuItem>
                          <MenuItem value="DRIVEAWAY">Driveaway</MenuItem>
                        </FormikTextField>
                      </Grid>

                      <Grid item={true} xs={12} sm={6}>
                        <FormikNumberField
                          name="search_criteria.vehicle_count"
                          label="Min. vehicles"
                          disabled={isSubmitting}
                          fullWidth={true}
                          inputProps={{
                            decimalScale: 0,
                            allowNegative: false,
                            allowLeadingZeros: false,
                            maxLength: 2,
                          }}
                        />
                      </Grid>

                      <Grid item={true} xs={12} sm={6}>
                        <FormikNumberField
                          name="search_criteria.max_vehicle_count"
                          label="Max. vehicles"
                          disabled={isSubmitting}
                          fullWidth={true}
                          inputProps={{
                            decimalScale: 0,
                            allowNegative: false,
                            allowLeadingZeros: false,
                            maxLength: 2,
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item={true} xs={12}>
                    <Grid container={true} spacing={2}>
                      <Grid item={true} xs={12}>
                        <Typography variant="h4">Payment</Typography>
                      </Grid>

                      <Grid item={true} xs={12}>
                        <FormikTextField
                          name="search_criteria.payment.terms"
                          label="Payment Options"
                          select={true}
                          disabled={isSubmitting}
                          fullWidth={true}
                          format={(value) => (value ? String(value) : '')}
                          parse={(event) => event.target.value || undefined}
                          SelectProps={{ displayEmpty: true }}
                          inputProps={{ 'data-testid': 'payment-terms' }}
                        >
                          <MenuItem value="">Any Payment</MenuItem>

                          {paymentGroups.map((terms, idx) => (
                            <MenuItem
                              key={idx}
                              value={formatPaymentTermsValue(terms)}
                            >
                              {(terms as unknown as string[]).includes(
                                'superpay',
                              ) ? (
                                <SuperPayIcon />
                              ) : (
                                formatPaymentTermsLabel(terms)
                              )}
                            </MenuItem>
                          ))}
                        </FormikTextField>
                      </Grid>

                      <Grid item={true} xs={12} sm={6}>
                        <FormikCurrencyField
                          name="search_criteria.price_per_km"
                          label="Min. vehicle price per mi"
                          fullWidth={true}
                          disabled={isSubmitting}
                          parse={(value) =>
                            value == null
                              ? value
                              : convertPricePerMileToPricePerKm(toFinite(value))
                          }
                          format={(value) =>
                            value == null
                              ? value
                              : convertPricePerKmToPricePerMile(value)
                          }
                          inputProps={{ allowNegative: false, decimalScale: 2 }}
                        />
                      </Grid>

                      <Grid item={true} xs={12} sm={6}>
                        <FormikCurrencyField
                          name="search_criteria.price"
                          label="Min. total price"
                          disabled={isSubmitting}
                          fullWidth={true}
                          inputProps={{ allowNegative: false, decimalScale: 2 }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item={true} xs={12}>
                    <Grid container={true} spacing={2}>
                      <Grid item={true} xs={12}>
                        <Typography variant="h4">Ready for Pickup</Typography>
                      </Grid>

                      <Grid item={true} xs={12}>
                        <AdvancedSearchDateField />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid
                    item={true}
                    xs={12}
                    data-intercom-target="shipper-filter"
                  >
                    <Grid container={true} spacing={2}>
                      <Grid item={true} xs={12}>
                        <Typography variant="h4">Shippers</Typography>
                      </Grid>

                      <Grid item={true} xs={12}>
                        <ShippersFilter disabled={!!isPrivateLoadboard} />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Collapse>
            </Grid>

            {MobileAppBridge.isInjected() && isFieldsExpanded && (
              <Styled1 item={true} xs={12}>
                <Button
                  variant="neutral"
                  fullWidth={true}
                  startIcon={<ArrowDropUp color="action" />}
                  onClick={() => {
                    expandFields(false);
                  }}
                >
                  Advanced Search
                </Button>
              </Styled1>
            )}
          </Grid>
        </Grid>

        <Grid item={true} xs={12}>
          <AlertSettingsSection
            mode={mode}
            isAlongRoute={isAlongRoute}
            onAddToSavedSearch={onAddToSavedSearch}
          />
        </Grid>
      </Grid>
    </FormikProvider>
  );
}
